import { CalendarOutlined, CreditCardOutlined, LeftOutlined, RightOutlined, TeamOutlined, UserOutlined } from "@ant-design/icons"
import { Badge, Spin, Typography } from "antd"
import React, { FC, ReactNode, useContext } from "react"
import { useTranslation } from "react-i18next"
import { Link, Redirect, Route, Switch, useHistory, useLocation } from "react-router-dom"
import QueryErrorAlert from "../../components/QueryErrorAlert"
import formatDate from "../../helpers/formatDate"
import { isSepaPaymentMethod } from "../../helpers/isSepaPaymentMethod"
import { useCurrency } from "../../helpers/currencyHelper"
import { CurrencyShort } from "../../helpers/renderAmount"
import { renderAsMoney } from "../../helpers/renderAsMoney"
import { getStoredBookerId } from "../../helpers/getStoredBookerId"
// useAccountBookerBookingsQuery wird nach dem Codegen aufgelöst (Query siehe account.graphql.ts).
import { GetCompanyQuery, useAccountBookerBookingsQuery, useGetUpdatedBookerQuery } from "../../generated/graphql"
import { URL_ACCOUNT, URL_ACCOUNT_ATTENDEES, URL_ACCOUNT_BOOKING_DETAILS, URL_ACCOUNT_BOOKINGS, URL_ACCOUNT_PAYMENT, URL_ACCOUNT_PROFILE } from "../../models/url"
import { SettingsContext } from "../../layouts/Main/MainLayout"
import RegistrationPanel from "../login/RegistrationPanel"
import AccountAttendees from "./AccountAttendees"
import AccountBookingDetails from "./AccountBookingDetails"
import AccountBookings, { BookingCard } from "./AccountBookings"
import AccountPayment from "./AccountPayment"
import { findNextUpcomingBooking, findNextUpcomingLesson, flattenBookerBookings, isPastBooking } from "./bookingStatus"
import css from "./AccountDashboard.module.css"

const { Title } = Typography

type AccountDashboardProps = {
    companySettings?: GetCompanyQuery["companyPublic"]
}

const formatLessonDate = (iso?: string | null): string | undefined => {
    if (!iso) return undefined
    const date = new Date(iso)
    if (Number.isNaN(date.getTime())) return undefined
    return formatDate(date, "dd.MM.yyyy · HH:mm")
}

export const maskIban = (iban?: string | null): string | undefined => {
    if (!iban) return undefined
    const trimmed = iban.replace(/\s+/g, "")
    if (trimmed.length < 4) return undefined
    return `•••• ${trimmed.slice(-4)}`
}

/**
 * Gemeinsame Kopfzeile für Unteransichten.
 *
 * Zurück-Verhalten:
 * - `onBack` gesetzt (z.B. Teilnehmer-Bearbeitung): ruft den Callback (zurück zur
 *   Liste statt aus der Ansicht heraus).
 * - sonst: In-App gedrillt (`fromAccountDashboard` vom Dashboard-Menü oder `fromUrl`
 *   von einer anderen Konto-Unterseite, z.B. Zahlung → Profil) → `history.goBack()`
 *   (kein History-Bounce); Deep-Link → `history.replace(URL_ACCOUNT)` (bleibt in der App).
 */
export const SubHeader: FC<{ title: string; onBack?: () => void; backLabel?: string }> = ({ title, onBack, backLabel }) => {
    const { t } = useTranslation()
    const history = useHistory()
    const location = useLocation<{ fromAccountDashboard?: boolean; fromUrl?: { pathname: string } }>()

    const handleBack = () => {
        if (onBack) return onBack()
        // fromUrl existiert nur nach einem In-App-Push → der vorherige History-Eintrag ist genau diese Seite
        if (location.state?.fromAccountDashboard || location.state?.fromUrl?.pathname?.startsWith(URL_ACCOUNT)) history.goBack()
        else history.replace(URL_ACCOUNT)
    }

    return (
        <div className={css.subHeader}>
            <button
                type='button'
                onClick={handleBack}
                className={css.backLink}
                aria-label={backLabel ?? t("account.backToDashboard", "Zurück zu meinem Konto")}
                data-testid='account-back'
            >
                <LeftOutlined />
            </button>
            <Title level={3} className={css.subTitle}>
                {title}
            </Title>
        </div>
    )
}

/**
 * Dashboard-Startansicht. Lädt Booker + Buchungen selbst und behandelt
 * Loading/Error nur für sich — so blockiert ein Fehler hier nicht die
 * Unterbereiche, die ihre Daten unabhängig laden.
 */
const AccountDashboardHome: FC = () => {
    const { t } = useTranslation()
    const currency = useCurrency().short as CurrencyShort
    const withdrawalEnabled = useContext(SettingsContext).withdrawalButtonEnabled === true
    const bookerId = getStoredBookerId()

    const { data: bookerData, loading: bookerLoading, error: bookerError, refetch: refetchBooker } = useGetUpdatedBookerQuery({
        skip: !bookerId,
        variables: { id: bookerId ?? "" },
        fetchPolicy: "network-only",
    })
    const { data: bookingsData, loading: bookingsLoading, error: bookingsError, refetch: refetchBookings } = useAccountBookerBookingsQuery({
        skip: !bookerId,
        variables: { bookerId: bookerId ?? "" },
        fetchPolicy: "network-only",
    })

    const booker = bookerData?.booker
    const bookings = flattenBookerBookings(bookingsData?.booker?.attendees)
    const now = new Date()

    if (bookerLoading || bookingsLoading) {
        return (
            <div className={css.loading} data-testid='account-dashboard-loading'>
                <Spin spinning />
            </div>
        )
    }

    if ((bookerError && !booker) || (bookingsError && !bookingsData)) {
        return (
            <div className={css.page}>
                <QueryErrorAlert
                    testId='account-dashboard-error'
                    message={t("account.loadError", "Dein Benutzerkonto konnte nicht geladen werden.")}
                    onRetry={() => Promise.all([refetchBooker(), refetchBookings()])}
                />
            </div>
        )
    }

    const attendees = booker?.attendees ?? []
    const activeBookings = bookings.filter((booking) => !isPastBooking(booking, now))
    const nextBooking = findNextUpcomingBooking(bookings, now)
    // Zukünftige Lektion (Datum + Ort) statt der ersten, ggf. vergangenen Lektion.
    const nextLesson = nextBooking ? findNextUpcomingLesson(nextBooking, now) : undefined
    const sepaIban = maskIban(booker?.paymentMethods?.find(isSepaPaymentMethod)?.iban)
    const bookerName = [booker?.firstname, booker?.lastname].filter(Boolean).join(" ") || undefined
    const credit = booker?.credit ?? 0

    type MenuItem = { key: string; to: string; icon: ReactNode; title: string; subtext?: string; count?: number }
    const menuItems: MenuItem[] = [
        {
            key: "bookings",
            to: URL_ACCOUNT_BOOKINGS,
            icon: <CalendarOutlined />,
            title: t("account.bookings.title", "Meine Buchungen"),
            subtext: formatLessonDate(nextLesson?.startDateTime),
            count: activeBookings.length,
        },
        {
            key: "attendees",
            to: URL_ACCOUNT_ATTENDEES,
            icon: <TeamOutlined />,
            title: t("account.attendees.title", "Teilnehmer"),
            subtext: attendees.map((a) => a?.firstname).filter(Boolean).join(", ") || undefined,
            count: attendees.length,
        },
        {
            key: "payment",
            to: URL_ACCOUNT_PAYMENT,
            icon: <CreditCardOutlined />,
            title: t("account.payment.title", "Zahlung & Guthaben"),
            subtext: sepaIban,
        },
        {
            key: "profile",
            to: URL_ACCOUNT_PROFILE,
            icon: <UserOutlined />,
            title: t("account.profile.title", "Profil"),
            subtext: bookerName,
        },
    ]

    const renderNextBookingCard = (): ReactNode => {
        if (!nextBooking) return null
        // Gleiche Kachel wie unter "Meine Buchungen" — identische Darstellung
        return (
            <section className={css.section} data-testid='account-next-booking'>
                <Title level={5} className={css.sectionTitle}>
                    {t("account.upNext", "Als Nächstes")}
                </Title>
                <BookingCard booking={nextBooking} nowMs={now.getTime()} withdrawalEnabled={withdrawalEnabled} />
            </section>
        )
    }

    const dashboardView = (
        <div className={css.page} data-testid='account-dashboard'>
            <div className={css.header}>
                <Title level={3} className={css.title}>
                    {t("account.title", "Mein Konto")}
                </Title>
                {credit > 0 && (
                    <Badge className={css.creditBadge} count={t("account.credit", "Guthaben") + ": " + renderAsMoney(credit, "", currency)} />
                )}
            </div>

            <nav className={css.menu}>
                {menuItems.map((item) => (
                    <Link
                        key={item.key}
                        to={{ pathname: item.to, state: { fromAccountDashboard: true } }}
                        className={css.menuRow}
                        data-testid={`account-menu-${item.key}`}
                    >
                        <span className={css.menuIcon}>{item.icon}</span>
                        <span className={css.menuBody}>
                            <span className={css.menuTitle}>{item.title}</span>
                            {item.subtext && <span className={css.menuSubtext}>{item.subtext}</span>}
                        </span>
                        {item.count !== undefined && item.count > 0 && <Badge count={item.count} className={css.menuCount} />}
                        <RightOutlined className={css.chevron} />
                    </Link>
                ))}
            </nav>

            {renderNextBookingCard()}
        </div>
    )

    return dashboardView
}

/**
 * Konto-Shell: routet die Unterbereiche. Jeder Bereich (inkl. der
 * Startansicht AccountDashboardHome) lädt seine Daten selbst — bewusst KEINE
 * gemeinsamen Queries hier, damit ein Fehler in einem Bereich die anderen nicht
 * blockiert.
 */
const AccountDashboard: FC<AccountDashboardProps> = ({ companySettings }) => {
    const { t } = useTranslation()
    return (
        <div className={css.shell}>
            <Switch>
                <Route exact path={URL_ACCOUNT}>
                    <AccountDashboardHome />
                </Route>
                <Route exact path={URL_ACCOUNT_BOOKINGS}>
                    <AccountBookings />
                </Route>
                <Route exact path={URL_ACCOUNT_BOOKING_DETAILS}>
                    <AccountBookingDetails />
                </Route>
                <Route exact path={URL_ACCOUNT_ATTENDEES}>
                    <AccountAttendees />
                </Route>
                <Route exact path={URL_ACCOUNT_PAYMENT}>
                    <AccountPayment />
                </Route>
                <Route exact path={URL_ACCOUNT_PROFILE}>
                    <div className={css.page}>
                        <SubHeader title={t("account.profile.title", "Profil")} />
                        <RegistrationPanel useCase='account' companySettings={companySettings} />
                    </div>
                </Route>
                <Route path={URL_ACCOUNT}>
                    <Redirect to={URL_ACCOUNT} />
                </Route>
            </Switch>
        </div>
    )
}

export default AccountDashboard
